import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, FolderIcon, FileTextIcon, ChevronDownIcon, CheckCircleIcon, LockIcon } from 'lucide-react';
import { coursesStructure, Module, Lesson } from '../data/courseData';
import { coursesData } from '../data/coursesData';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../contexts/AuthContext';
interface LessonNavigationControllerProps {
  currentCourseId?: string;
  currentModuleId: string;
  currentLessonId: string;
}
export function LessonNavigationController({
  currentCourseId,
  currentModuleId,
  currentLessonId
}: LessonNavigationControllerProps) {
  const navigate = useNavigate();
  const {
    isLessonComplete
  } = useProgress();
  const {
    unlockedAll
  } = useAuth();
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [showLessonDropdown, setShowLessonDropdown] = useState(false);
  // Find current course, module, and lesson
  const currentCourse = coursesStructure.find(c => c.id === currentCourseId);
  const currentModule = currentCourse?.modules.find(m => m.id === currentModuleId);
  const currentLesson = currentModule?.lessons.find(l => l.id === currentLessonId);
  // Show all 10 courses from coursesData
  const allCourses = coursesData;
  const handleCourseChange = (course: (typeof coursesData)[0]) => {
    // Allow navigation if course is active OR if all courses are unlocked
    if (course.status !== 'active' && !unlockedAll) {
      return;
    }
    setShowCourseDropdown(false);
    // Find the detailed course structure
    const detailedCourse = coursesStructure.find(c => c.id === course.id);
    if (!detailedCourse) return;
    const firstModule = detailedCourse.modules[0];
    const firstLesson = firstModule.lessons[0];
    navigate(`/curs/${course.id}/modul/${firstModule.id}/lectie/${firstLesson.id}`);
  };
  const handleModuleChange = (module: Module) => {
    setShowModuleDropdown(false);
    const firstLesson = module.lessons[0];
    if (currentCourseId) {
      navigate(`/curs/${currentCourseId}/modul/${module.id}/lectie/${firstLesson.id}`);
    } else {
      navigate(`/modul/${module.id}/lectie/${firstLesson.id}`);
    }
  };
  const handleLessonChange = (lesson: Lesson) => {
    setShowLessonDropdown(false);
    if (currentCourseId) {
      navigate(`/curs/${currentCourseId}/modul/${currentModuleId}/lectie/${lesson.id}`);
    } else {
      navigate(`/modul/${currentModuleId}/lectie/${lesson.id}`);
    }
  };
  return <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border border-gray-200" data-id="element-1244">
      <div className="flex flex-col md:flex-row md:items-center gap-3" data-id="element-1245">
        {/* Course Selector */}
        <div className="flex-1 relative" data-id="element-1246">
          <button onClick={() => {
          setShowCourseDropdown(!showCourseDropdown);
          setShowModuleDropdown(false);
          setShowLessonDropdown(false);
        }} className="w-full flex items-center justify-between space-x-2 bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-3 transition-colors text-left" data-id="element-1247">
            <div className="flex items-center space-x-2 flex-1 min-w-0" data-id="element-1248">
              <BookOpenIcon className="w-4 h-4 text-[#357BEB] flex-shrink-0" data-id="element-1249" />
              <div className="flex-1 min-w-0" data-id="element-1250">
                <p className="text-xs text-gray-500 mb-0.5" data-id="element-1251">Curs</p>
                <p className="text-sm font-semibold text-[#132C53] truncate" data-id="element-1252">
                  {currentCourse?.title || 'Selectează curs'}
                </p>
              </div>
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showCourseDropdown ? 'rotate-180' : ''}`} data-id="element-1253" />
          </button>
          <AnimatePresence data-id="element-1254">
            {showCourseDropdown && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto" data-id="element-1255">
                <div className="p-2" data-id="element-1256">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1 mb-1" data-id="element-1257">
                    Toate cursurile ({allCourses.length})
                  </p>
                  {allCourses.map(course => {
                // Check if course is locked and if unlockedAll overrides it
                const isLocked = course.status === 'locked' && !unlockedAll;
                const isCurrent = course.id === currentCourseId;
                return <button key={course.id} onClick={() => handleCourseChange(course)} disabled={isLocked} className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${isLocked ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'} ${isCurrent ? 'bg-blue-50 border border-blue-200' : ''}`} data-id="element-1258">
                        <div className="flex items-start justify-between" data-id="element-1259">
                          <div className="flex-1 min-w-0" data-id="element-1260">
                            <p className={`text-sm font-semibold mb-1 ${isLocked ? 'text-gray-500' : 'text-[#132C53]'}`} data-id="element-1261">
                              {course.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1262">
                              {course.moduleCount} module • {course.lessonCount}{' '}
                              lecții
                            </p>
                          </div>
                          {isLocked && <LockIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" data-id="element-1263" />}
                          {!isLocked && isCurrent && <CheckCircleIcon className="w-4 h-4 text-blue-500 flex-shrink-0 ml-2" data-id="element-1264" />}
                        </div>
                      </button>;
              })}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
        {/* Module Selector */}
        {currentCourse && <div className="flex-1 relative" data-id="element-1265">
            <button onClick={() => {
          setShowModuleDropdown(!showModuleDropdown);
          setShowCourseDropdown(false);
          setShowLessonDropdown(false);
        }} className="w-full flex items-center justify-between space-x-2 bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-3 transition-colors text-left" data-id="element-1266">
              <div className="flex items-center space-x-2 flex-1 min-w-0" data-id="element-1267">
                <FolderIcon className="w-4 h-4 text-[#357BEB] flex-shrink-0" data-id="element-1268" />
                <div className="flex-1 min-w-0" data-id="element-1269">
                  <p className="text-xs text-gray-500 mb-0.5" data-id="element-1270">Modul</p>
                  <p className="text-sm font-semibold text-[#132C53] truncate" data-id="element-1271">
                    {currentModule?.title || 'Selectează modul'}
                  </p>
                </div>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showModuleDropdown ? 'rotate-180' : ''}`} data-id="element-1272" />
            </button>
            <AnimatePresence data-id="element-1273">
              {showModuleDropdown && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto" data-id="element-1274">
                  {currentCourse.modules.map(module => <button key={module.id} onClick={() => handleModuleChange(module)} className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${module.id === currentModuleId ? 'bg-blue-50' : ''}`} data-id="element-1275">
                      <p className="text-sm font-semibold text-[#132C53] mb-1" data-id="element-1276">
                        {module.title}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1277">
                        {module.lessons.length} lecții
                      </p>
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>}
        {/* Lesson Selector */}
        {currentModule && <div className="flex-1 relative" data-id="element-1278">
            <button onClick={() => {
          setShowLessonDropdown(!showLessonDropdown);
          setShowCourseDropdown(false);
          setShowModuleDropdown(false);
        }} className="w-full flex items-center justify-between space-x-2 bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-3 transition-colors text-left" data-id="element-1279">
              <div className="flex items-center space-x-2 flex-1 min-w-0" data-id="element-1280">
                <FileTextIcon className="w-4 h-4 text-[#357BEB] flex-shrink-0" data-id="element-1281" />
                <div className="flex-1 min-w-0" data-id="element-1282">
                  <p className="text-xs text-gray-500 mb-0.5" data-id="element-1283">Lecție</p>
                  <p className="text-sm font-semibold text-[#132C53] truncate" data-id="element-1284">
                    {currentLesson?.title || 'Selectează lecție'}
                  </p>
                </div>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${showLessonDropdown ? 'rotate-180' : ''}`} data-id="element-1285" />
            </button>
            <AnimatePresence data-id="element-1286">
              {showLessonDropdown && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 overflow-y-auto" data-id="element-1287">
                  {currentModule.lessons.map(lesson => {
              const isComplete = currentCourseId && isLessonComplete(currentCourseId, currentModuleId, lesson.id);
              return <button key={lesson.id} onClick={() => handleLessonChange(lesson)} className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${lesson.id === currentLessonId ? 'bg-blue-50' : ''}`} data-id="element-1288">
                        <div className="flex items-start justify-between" data-id="element-1289">
                          <div className="flex-1 min-w-0" data-id="element-1290">
                            <p className="text-sm font-semibold text-[#132C53] mb-1" data-id="element-1291">
                              {lesson.title}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1292">
                              {lesson.duration}
                            </p>
                          </div>
                          {isComplete && <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 ml-2" data-id="element-1293" />}
                        </div>
                      </button>;
            })}
                </motion.div>}
            </AnimatePresence>
          </div>}
      </div>
    </div>;
}